import { useState, useEffect, useCallback } from 'react';
import KPICard from '../components/KPICard';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { printDPA, printAttestation } from '../utils/printDocument';

const C = {
  bg: '#12141f', card: '#1a1d2e', border: '#2a2d3e',
  text: '#e2e8f0', muted: '#94a3b8', faint: '#64748b',
  accent: '#41B073', green: '#22c55e', red: '#ef4444',
  orange: '#f97316', yellow: '#eab308',
};
const inputStyle = { padding: '8px 12px', borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };

const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const STATUT_RGPD = {
  en_cours:    { label: 'En cours',            color: '#41B073' },
  a_supprimer: { label: 'À supprimer',         color: '#f97316' },
  restitue:    { label: 'Données restituées',  color: '#3b82f6' },
  supprime:    { label: 'Données supprimées ✓', color: '#22c55e' },
};

const Badge = ({ text, color }) => (
  <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{text}</span>
);

// ─── Alerte RGPD ─────────────────────────────────────────────────────────────
function alerteColor(days) {
  if (days >= 30) return C.red;
  if (days >= 25) return C.orange;
  return null;
}
function alerteLabel(days) {
  if (days >= 30) return `🔴 J+${days} — Suppression requise`;
  if (days >= 25) return `⚠️ J+${days} — Suppression dans ${30 - days} jour(s)`;
  return null;
}

// ─── Panel mission ────────────────────────────────────────────────────────────
function MissionPanel({ missionId, onClose, onSave, clients }) {
  const fetchWithAuth = useAuthFetch();
  const [mission, setMission] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    fetchWithAuth(`/api/missions/${missionId}`).then(r => r.json()).then(setMission);
  }, [missionId]);

  useEffect(() => { load(); }, [load]);

  const setStatut = async (rgpd_statut) => {
    setSaving(true);
    await fetchWithAuth(`/api/missions/${missionId}/statut`, { method: 'PATCH', body: JSON.stringify({ rgpd_statut }) });
    load(); setSaving(false); onSave?.();
  };

  const del = async () => {
    if (!window.confirm('Supprimer cette mission ?')) return;
    await fetchWithAuth(`/api/missions/${missionId}`, { method: 'DELETE' });
    onClose(); onSave?.();
  };

  if (!mission) return null;
  const days   = mission.days_since_end;
  const alerte = typeof days === 'number' && days >= 25 && mission.rgpd_statut !== 'supprime';
  const statut = STATUT_RGPD[mission.rgpd_statut] || { label: mission.rgpd_statut, color: C.faint };
  const client = clients.find(c => c.id === mission.client_id) || {};

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, width: 480, height: '100vh', background: C.card, borderLeft: `1px solid ${C.border}`, zIndex: 100, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{mission.titre}</div>
              <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>{mission.client_nom}</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.faint, cursor: 'pointer', fontSize: 20 }}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Badge text={statut.label} color={statut.color} />
            {alerte && <Badge text={alerteLabel(days)} color={alerteColor(days)} />}
          </div>
        </div>

        <div className="modal-scroll" style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {/* Dates */}
          <div className="form-grid" style={{ marginBottom: 16 }}>
            {[['📅 Début', fmtDate(mission.date_debut)], ['🏁 Fin', fmtDate(mission.date_fin)]].map(([l, v]) => (
              <div key={l} style={{ background: C.bg, borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 11, color: C.faint, marginBottom: 2 }}>{l}</div>
                <div style={{ fontSize: 13, color: C.text }}>{v}</div>
              </div>
            ))}
          </div>

          {/* RGPD dates */}
          <div style={{ background: C.bg, border: `1px solid ${alerte ? alerteColor(days) + '55' : C.border}`, borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: alerte ? alerteColor(days) : C.faint, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>🔒 Données RGPD</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
              <span style={{ color: C.faint }}>Date limite suppression</span>
              <span style={{ color: alerte ? alerteColor(days) : C.text, fontWeight: alerte ? 700 : 400 }}>{fmtDate(mission.rgpd_date_suppression)}</span>
            </div>
            {mission.rgpd_date_suppression_effective && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: C.faint }}>Suppression effective</span>
                <span style={{ color: C.green }}>{fmtDate(mission.rgpd_date_suppression_effective)}</span>
              </div>
            )}
          </div>

          {/* Sources de données */}
          {(mission.rgpd_sources || []).length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.faint, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sources de données</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    {['Source', 'Type', 'Données perso ?'].map(h => (
                      <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: C.faint, fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mission.rgpd_sources.map((s, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}22` }}>
                      <td style={{ padding: '8px', color: C.text }}>{s.source}</td>
                      <td style={{ padding: '8px', color: C.muted }}>{s.type || '—'}</td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ color: s.donnees_perso ? C.orange : C.green, fontWeight: 600 }}>
                          {s.donnees_perso ? '⚠️ Oui' : '✓ Non'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {mission.description && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.faint, marginBottom: 6, textTransform: 'uppercase' }}>Description</div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>{mission.description}</div>
            </div>
          )}
          {mission.notes && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.faint, marginBottom: 6, textTransform: 'uppercase' }}>Notes</div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>{mission.notes}</div>
            </div>
          )}

          {/* Changement de statut RGPD */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.faint, marginBottom: 8, textTransform: 'uppercase' }}>Cycle de vie des données</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {Object.entries(STATUT_RGPD).map(([key, s]) => (
                <button key={key} disabled={saving || mission.rgpd_statut === key} onClick={() => setStatut(key)}
                  style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${s.color}44`, background: mission.rgpd_statut === key ? s.color + '22' : 'none', color: mission.rgpd_statut === key ? s.color : C.muted, cursor: mission.rgpd_statut === key ? 'default' : 'pointer', fontSize: 12, fontFamily: 'inherit', textAlign: 'left', fontWeight: mission.rgpd_statut === key ? 600 : 400 }}>
                  {mission.rgpd_statut === key ? '● ' : '○ '}{s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 8 }}>
          <button onClick={() => printDPA(mission, client)} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.accent}44`, background: 'none', color: C.accent, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>📃 DPA</button>
          {mission.rgpd_statut === 'supprime' && (
            <button onClick={() => printAttestation(mission, client)} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.green}44`, background: 'none', color: C.green, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>✅ Attestation</button>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={del} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.red}44`, background: 'none', color: C.red, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>🗑</button>
        </div>
      </div>
    </>
  );
}

// ─── Modal création / édition ─────────────────────────────────────────────────
function MissionModal({ initial, clients, onSave, onClose }) {
  const [form, setForm] = useState(initial || { rgpd_statut: 'en_cours', rgpd_sources: [] });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addSource = () => setForm(f => ({ ...f, rgpd_sources: [...(f.rgpd_sources || []), { source: '', type: '', donnees_perso: false }] }));
  const setSource = (i, k, v) => setForm(f => ({ ...f, rgpd_sources: f.rgpd_sources.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const delSource = (i) => setForm(f => ({ ...f, rgpd_sources: f.rgpd_sources.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.client_id || !form.titre) return;
    setSaving(true);
    try {
      const url    = initial?.id ? `/api/missions/${initial.id}` : '/api/missions';
      const method = initial?.id ? 'PUT' : 'POST';
      await onSave(url, method, form);
      onClose();
    } finally { setSaving(false); }
  };

  const labelStyle = { display: 'block', fontSize: 11, color: C.faint, marginBottom: 4, textTransform: 'uppercase' };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200 }} />
      <div className="modal-scroll" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 540, maxHeight: '85vh', overflowY: 'auto', background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, zIndex: 201 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: C.text }}>{initial?.id ? 'Modifier la mission' : 'Nouvelle mission'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.faint, cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Client *</label>
          <select value={form.client_id || ''} onChange={e => set('client_id', parseInt(e.target.value))} style={inputStyle}>
            <option value="">Sélectionner…</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Titre *</label>
          <input value={form.titre || ''} onChange={e => set('titre', e.target.value)} style={inputStyle} placeholder="Ex : Dashboard commercial Q1 2025" />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Description</label>
          <textarea value={form.description || ''} onChange={e => set('description', e.target.value)} style={{ ...inputStyle, height: 60, resize: 'vertical' }} />
        </div>

        <div className="form-grid" style={{ marginBottom: 12 }}>
          {[['date_debut', 'Date de début'], ['date_fin', 'Date de fin']].map(([k, l]) => (
            <div key={k}>
              <label style={labelStyle}>{l}</label>
              <input type="date" value={form[k] || ''} onChange={e => set(k, e.target.value)} style={inputStyle} />
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Statut RGPD</label>
          <select value={form.rgpd_statut || 'en_cours'} onChange={e => set('rgpd_statut', e.target.value)} style={inputStyle}>
            {Object.entries(STATUT_RGPD).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
          </select>
        </div>

        {/* Sources de données */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ ...labelStyle, margin: 0 }}>Sources de données traitées</label>
            <button type="button" onClick={addSource} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'none', color: C.muted, cursor: 'pointer', fontFamily: 'inherit' }}>+ Ajouter</button>
          </div>
          {(form.rgpd_sources || []).map((s, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <input placeholder="Source (ex : CRM)" value={s.source} onChange={e => setSource(i, 'source', e.target.value)} style={{ ...inputStyle, padding: '6px 10px' }} />
              <input placeholder="Type (RH, Clients…)" value={s.type} onChange={e => setSource(i, 'type', e.target.value)} style={{ ...inputStyle, padding: '6px 10px' }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: C.muted, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!s.donnees_perso} onChange={e => setSource(i, 'donnees_perso', e.target.checked)} />
                Perso
              </label>
              <button onClick={() => delSource(i)} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 14 }}>✕</button>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Notes</label>
          <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} style={{ ...inputStyle, height: 60, resize: 'vertical' }} />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={save} disabled={saving || !form.client_id || !form.titre} style={{ flex: 1, padding: '10px', borderRadius: 8, background: C.accent, border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
            {saving ? 'Enregistrement…' : initial?.id ? '💾 Enregistrer' : '+ Créer la mission'}
          </button>
          <button onClick={onClose} style={{ padding: '10px 18px', borderRadius: 8, background: 'none', border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Annuler</button>
        </div>
      </div>
    </>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function MissionsPage() {
  const fetchWithAuth = useAuthFetch();
  const [missions, setMissions] = useState([]);
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const LIMIT = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT, sort: 'created_at', order: 'desc' });
      if (search)       params.set('q', search);
      if (filterStatut) params.set('rgpd_statut', filterStatut);
      const [res, all] = await Promise.all([
        fetchWithAuth(`/api/missions?${params}`).then(r => r.json()),
        fetchWithAuth('/api/missions?limit=999').then(r => r.json()),
      ]);
      setMissions(res.data || []); setTotal(res.total || 0);
      const counts = { total: 0, en_cours: 0, a_supprimer: 0, restitue: 0, supprime: 0, urgences: 0 };
      (all.data || []).forEach(m => {
        counts.total++;
        if (counts[m.rgpd_statut] !== undefined) counts[m.rgpd_statut]++;
        if (m.days_since_end >= 30 && m.rgpd_statut !== 'supprime') counts.urgences++;
      });
      setKpis(counts);
    } finally { setLoading(false); }
  }, [search, page, filterStatut]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetchWithAuth('/api/clients?limit=500').then(r => r.json()).then(d => setClients(d.data || []));
  }, []);

  const handleSave = async (url, method, form) => {
    await fetchWithAuth(url, { method, body: JSON.stringify(form) });
    load();
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>Missions & RGPD</h1>
        <button onClick={() => setShowModal(true)} style={{ padding: '9px 18px', borderRadius: 8, background: C.accent, border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>+ Nouvelle mission</button>
      </div>

      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <KPICard title="Total" value={kpis.total || 0} color={C.accent} icon="📁" onClick={() => setFilterStatut('')} />
        <KPICard title="En cours" value={kpis.en_cours || 0} color={C.accent} onClick={() => setFilterStatut('en_cours')} />
        <KPICard title="À supprimer" value={kpis.a_supprimer || 0} color={C.orange} onClick={() => setFilterStatut('a_supprimer')} />
        <KPICard title="Supprimées" value={kpis.supprime || 0} color={C.green} onClick={() => setFilterStatut('supprime')} />
        <KPICard title="🔴 Urgences J+30" value={kpis.urgences || 0} color={C.red} alert={kpis.urgences > 0} onClick={() => {}} />
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="🔍 Rechercher…" style={{ ...inputStyle, maxWidth: 280 }} />
        <select value={filterStatut} onChange={e => { setFilterStatut(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: 200, cursor: 'pointer' }}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUT_RGPD).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['Client', 'Mission', 'Début', 'Fin', 'Limite suppression', 'J depuis fin', 'Statut RGPD'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: C.faint, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: C.faint }}>Chargement…</td></tr>
              ) : missions.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: C.faint }}>Aucune mission</td></tr>
              ) : missions.map((m, i) => {
                const days  = m.days_since_end;
                const color = (typeof days === 'number' && days >= 25 && m.rgpd_statut !== 'supprime') ? alerteColor(days) : null;
                const statut = STATUT_RGPD[m.rgpd_statut] || { label: m.rgpd_statut, color: C.faint };
                return (
                  <tr key={m.id} onClick={() => setSelectedId(m.id)}
                    style={{ borderBottom: `1px solid ${C.border}`, cursor: 'pointer', background: color ? color + '10' : (i % 2 === 0 ? 'transparent' : '#ffffff05') }}
                    onMouseEnter={e => e.currentTarget.style.background = C.accent + '15'}
                    onMouseLeave={e => e.currentTarget.style.background = color ? color + '10' : (i % 2 === 0 ? 'transparent' : '#ffffff05')}>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: C.text, fontWeight: 500 }}>{m.client_nom}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: C.muted }}>{m.titre}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: C.faint }}>{fmtDate(m.date_debut)}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: C.faint }}>{fmtDate(m.date_fin)}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: color || C.faint, fontWeight: color ? 700 : 400 }}>{fmtDate(m.rgpd_date_suppression)}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: color || C.muted, fontWeight: color ? 700 : 400 }}>
                      {typeof days === 'number' && days >= 0 ? `J+${days}` : '—'}
                    </td>
                    <td style={{ padding: '11px 14px' }}><Badge text={statut.label} color={statut.color} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 13, color: C.faint }}>{total} mission{total !== 1 ? 's' : ''}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '5px 12px', borderRadius: 6, background: 'none', border: `1px solid ${C.border}`, color: page === 1 ? C.border : C.muted, cursor: page === 1 ? 'default' : 'pointer', fontSize: 13 }}>←</button>
            <span style={{ padding: '5px 12px', fontSize: 13, color: C.muted }}>Page {page} / {totalPages || 1}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ padding: '5px 12px', borderRadius: 6, background: 'none', border: `1px solid ${C.border}`, color: page >= totalPages ? C.border : C.muted, cursor: page >= totalPages ? 'default' : 'pointer', fontSize: 13 }}>→</button>
          </div>
        </div>
      </div>

      {selectedId && <MissionPanel missionId={selectedId} onClose={() => setSelectedId(null)} onSave={load} clients={clients} />}
      {showModal && <MissionModal clients={clients} onSave={handleSave} onClose={() => setShowModal(false)} />}
    </div>
  );
}
